/**
 * Estilos compartidos de formularios auth (login, recovery, setup) — línea ticket.
 */
import { Platform, StyleSheet } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';

/** Ink sobre botón gold. */
export const AUTH_ACCENT_INK = ticketColors.goldInk;

export const authFormUi = StyleSheet.create({
  scrollCenter: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.xl,
    minHeight: '100%',
  },
  card: {
    width: '100%',
    backgroundColor: ticketColors.paper,
    borderRadius: 28,
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.xl,
    gap: 20,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  brandBlock: {
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.titleXL,
    fontSize: 28,
    letterSpacing: -0.6,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  titleCentered: {
    ...typography.titleXL,
    fontSize: 28,
    letterSpacing: -0.6,
    color: ticketColors.ink,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  subtitle: {
    ...typography.body,
    fontSize: 14,
    lineHeight: 21,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  subtitleCentered: {
    ...typography.body,
    fontSize: 14,
    lineHeight: 21,
    color: ticketColors.ink3,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  fieldGroup: {
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  input: {
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    paddingHorizontal: spacing.lg,
    paddingVertical: Platform.OS === 'web' ? 13 : 14,
    minHeight: 48,
    color: ticketColors.ink,
    fontSize: 15,
    fontFamily: ticketFonts.sans,
  },
  inputError: {
    borderColor: 'rgba(255,107,107,0.55)',
  },
  passwordRow: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: 48,
  },
  eyeButton: {
    position: 'absolute',
    right: 6,
    top: 0,
    bottom: 0,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorBanner: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(255,107,107,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255,107,107,0.28)',
  },
  errorBannerText: {
    ...typography.caption,
    color: ticketColors.bad,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  errorPlain: {
    ...typography.caption,
    color: ticketColors.bad,
    fontFamily: ticketFonts.sans,
  },
  primaryBtn: {
    minHeight: 52,
    borderRadius: 18,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
  },
  primaryBtnDisabled: {
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    opacity: 1,
  },
  primaryLabel: {
    ...typography.body,
    fontSize: 15,
    color: AUTH_ACCENT_INK,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  primaryLabelDisabled: {
    color: ticketColors.ink3,
  },
  links: {
    alignItems: 'center',
    gap: 10,
  },
  linkLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  /** Links secundarios (invite): menos peso visual que Registrarme / Olvidé. */
  linkLabelMuted: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  linkLabelAccent: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  inviteHintBanner: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: 4,
  },
  inviteHintBannerText: {
    ...typography.caption,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },

  backBtn: {
    alignSelf: 'flex-start',
    marginTop: -spacing.xs,
  },
  backLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  passwordHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginTop: 2,
  },
  passwordHintDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: ticketColors.gold,
  },
  passwordHintDotOk: {
    backgroundColor: ticketColors.ok,
  },
  passwordHintText: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  passwordHintTextOk: {
    color: ticketColors.ok,
  },
  legal: {
    ...typography.caption,
    fontSize: 12,
    lineHeight: 18,
    color: ticketColors.ink3,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  legalLink: {
    color: ticketColors.gold,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  separator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  separatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: ticketColors.rule,
  },
  separatorText: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  ssoLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    marginBottom: spacing.sm,
    fontFamily: ticketFonts.sans,
  },
  ssoRow: {
    flexDirection: 'row',
    gap: 10,
  },
  ssoButton: {
    flex: 1,
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    backgroundColor: ticketColors.paper,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  ssoLabelText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
